import {useRequiredConfig} from '@domain/shifthandover'
import {
  CategoryDropDown,
  PriorityDropDown,
  ProcessStageDropDown,
  EventProcessStageAutocomplete
} from '@hconnect/common/components/shiftEventFormFields'
import {EventHighlight} from '@hconnect/common/components/shiftEventFormFields/EventHighlight'
import {REGULAR_SIZE, INPUT_FORM_FIELD_SX} from '@hconnect/common/consts'
import {EquipmentIdType, EventType, Priority, BaseEvent, ShiftEvent} from '@hconnect/common/types'
import {Grid, TextField, Box} from '@mui/material'
import {EventDescriptionBox} from '@widgets/common-shift-event'
import {omit} from 'lodash'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {isEventWithStatusNone} from '../../common/utils/eventStatus'
import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'
import {PossibleErrors, SharedEventStructure} from '../../types/shareEventForm.types'
import {EquipmentSearchContainer} from '../eventProperties/EquipmentSearchContainer'
import {EventInfo} from '../eventProperties/EventInfo'
import {EventStatusDropDown} from '../eventProperties/EventStatus'
import {EventTypeDropdownBox} from '../eventProperties/EventTypeDropdownBox'
import {ScheduleSwitch} from '../eventProperties/ScheduleSwitch'

type Props = {
  plantId: string
  item: Partial<Omit<SharedEventStructure, 'eventType'>> & {eventType: EventType}
  update: <K extends keyof SharedEventStructure>(
    key: K,
    value: SharedEventStructure[K] | EventType
  ) => void
  updateMultiPart: (delta) => void
  validationError: Map<PossibleErrors, string>
  children: React.ReactNode
  equipmentIdType?: EquipmentIdType
  autocompleteProcessStage?: boolean
  isEditMode: boolean
}

export const eventDefaultObject = (
  eventType: SharedEventStructure['eventType'],
  initialData: Partial<ShiftEvent> = {}
): Partial<ShiftEvent> => {
  if (
    eventType === 'information' ||
    eventType === 'idea' ||
    eventType === 'quality' ||
    eventType === 'healthAndSafety' ||
    eventType === 'environment'
  ) {
    return {
      description: '',
      eventType,
      status: 'none',
      title: '',
      ...omit(initialData, 'eventType')
    } as Partial<ShiftEvent>
  } // else
  return {
    description: '',
    eventType,
    status: 'pending',
    title: '',
    ...omit(initialData, 'eventType')
  } as Partial<ShiftEvent>
}

/**
 * if you consider using this component, check first src/container/EventFormContainer.tsx,
 * as it handles the validation and the stuff around this form,
 * it might already solve all your needs
 */
/* eslint-disable complexity */
export const SharedEventForm: React.FC<Props> = ({
  plantId,
  children,
  item,
  update,
  updateMultiPart,
  validationError,
  equipmentIdType,
  isEditMode,
  autocompleteProcessStage
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const getErrorText = (key: PossibleErrors) => {
    const errorKey = validationError.get(key)
    return errorKey && t(errorKey)
  }

  const {workOrderTarget} = useRequiredConfig(plantId)

  return (
    <>
      <Box display="flex" gap={2} mb={2}>
        <TextField
          data-test-id="event-form-title"
          error={validationError.has('title')}
          fullWidth={true}
          helperText={getErrorText('title')}
          label={t('shiftEvent.label.title')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            update('title', event.target.value)
          }
          required
          value={item.title}
          variant="filled"
          inputProps={{'data-test-id': 'event-form-title-input'}}
          InputProps={INPUT_FORM_FIELD_SX}
        />
        <EventHighlight
          title={t('eventsTable.action.highlight')}
          value={item.isHighlighted}
          onChange={(value) => update('isHighlighted', value)}
        />
      </Box>

      {'created' in item && (
        <EventInfo
          item={item as Partial<SharedEventStructure>}
          plantId={plantId}
          workOrderTarget={workOrderTarget}
        />
      )}

      <Box my={1}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item {...REGULAR_SIZE}>
            <EventTypeDropdownBox
              item={item as BaseEvent}
              value={item.eventType}
              updateMultiPart={updateMultiPart}
            />
          </Grid>

          <Grid item {...REGULAR_SIZE}>
            <EventStatusDropDown
              translate={t}
              allow={
                isEventWithStatusNone(item as BaseEvent) && isEditMode
                  ? ['none', 'cancelled']
                  : isEventWithStatusNone(item as BaseEvent) && !isEditMode
                    ? ['none']
                    : undefined
              }
              data-test-id="event-form-status"
              errorText={getErrorText('status')}
              onChange={(value) => update('status', value)}
              required
              value={item.status}
            />
          </Grid>

          <Grid item xs={12}>
            <EquipmentSearchContainer
              plantId={plantId}
              data-test-id="event-form-equipmentNumber"
              errorText={getErrorText('equipmentNumber')}
              updateMultiPart={updateMultiPart}
              equipmentIdType={equipmentIdType}
              value={item as BaseEvent}
            />
          </Grid>

          {autocompleteProcessStage ? (
            <Grid item xs={12}>
              <EventProcessStageAutocomplete
                plantId={plantId}
                data-test-id="event-form-processStage"
                errorText={getErrorText('processStage')}
                onChange={(value?: string) => update('processStage', value)}
                value={item.processStage}
              />
            </Grid>
          ) : (
            <Grid item {...REGULAR_SIZE}>
              <ProcessStageDropDown
                translate={t}
                plantId={plantId}
                data-test-id="event-form-processStage"
                errorText={getErrorText('processStage')}
                onChange={(value) => update('processStage', value)}
                value={item.processStage}
              />
            </Grid>
          )}
          <Grid item {...REGULAR_SIZE}>
            <CategoryDropDown
              translate={t}
              data-test-id="event-form-category"
              errorText={getErrorText('category')}
              onChange={(value) => update('category', value)}
              required
              value={item.category}
            />
          </Grid>

          <Grid item {...REGULAR_SIZE}>
            <PriorityDropDown
              translate={t}
              data-test-id="event-form-priority"
              errorText={getErrorText('priority')}
              onChange={(value) => update('priority', value)}
              value={item.priority as Priority}
            />
          </Grid>

          <ScheduleSwitch
            plantId={plantId}
            label={t('shiftEvent.action.schedule')}
            item={item}
            onChange={(value) => update('schedule', value)}
            errorText={getErrorText('schedule')}
          />

          <Grid item xs={12}>
            <EventDescriptionBox
              current={item.description || ''}
              errorCode={validationError.get('description')}
              onChange={(next) => update('description', next)}
              required
            />
          </Grid>
        </Grid>
      </Box>
      {children}
    </>
  )
}
